import { useSEO } from "@/hooks/use-seo";
import { seoFor } from "@/data/marketing-routes";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { Mail, MapPin, Building2, Users, Target, Zap } from "lucide-react";

export default function AboutPage() {
  useSEO(seoFor("/about"));

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PublicHeader />
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
        <section className="pt-28 md:pt-32 pb-12 md:pb-20">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-testid="text-about-title">
              About PadPulse
            </h1>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto" data-testid="text-about-subtitle">
              Built by PadSplit hosts, for PadSplit hosts.
            </p>
          </div>
        </section>
      </div>

      <main className="flex-grow">
        <section className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  PadPulse was created because PadSplit hosts lacked what Airbnb hosts have had for years: a dedicated management platform built for how they actually operate. While vacation rental operators could choose from dozens of purpose-built PMSs, PadSplit hosts were managing their portfolios across a patchwork of spreadsheets, apps, and manual processes.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We set out to build the definitive platform for PadSplit operators — one that speaks the language of co-living, integrates natively with PadSplit, and empowers hosts to grow their portfolio without growing their workload.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Building2, title: "Property Management", desc: "Full portfolio oversight from one dashboard" },
                  { icon: Users, title: "Team Collaboration", desc: "Role-based access for your entire team" },
                  { icon: Target, title: "Underwriting Tools", desc: "Data-driven deal analysis and modeling" },
                  { icon: Zap, title: "Automation", desc: "Pricing, messaging, and task automation" },
                ].map((item) => (
                  <div key={item.title} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <item.icon className="text-brand mb-2" size={20} />
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-16">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Contact Us</h2>
              <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                <div className="flex items-start gap-4 p-6 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-brand" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a
                      href="mailto:info@padpulse.io"
                      className="text-brand hover:underline text-sm"
                      data-testid="link-about-email"
                    >
                      info@padpulse.io
                    </a>
                    <p className="text-xs text-gray-500 mt-1">We typically respond within 1–2 business days.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-6 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="w-10 h-10 rounded-lg bg-brand-accent/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-brand-accent" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                    <p className="text-sm text-gray-600" data-testid="text-about-address">
                      AAZ TXRP3 LLC<br />
                      539 W. Commerce St #304<br />
                      Dallas, TX 75208
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
